import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ArrowRight, Loader2, Copy, Eye, Zap } from "lucide-react";
import { toast } from "sonner";
import { API, LOGO_URL } from "@/config";
import { CLAIMS } from "@/config/claims";

const TYPE_LABELS = {
  blog: "Blog Post", social: "Social Media", ad_copy: "Ad Copy",
  twitter: "Twitter Thread", instagram: "Instagram Caption", tiktok: "TikTok Script",
};

export default function PublicDemoPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/demos/by-slug/${slug}`)
      .then((r) => setDemo(r.data))
      .catch(() => setDemo(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
    </div>
  );

  if (!demo) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">This page is no longer available</h1>
        <Link to="/"><Button className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="demo-notfound-home-btn">Go to Creatara</Button></Link>
      </div>
    </div>
  );

  const pageUrl = `https://creatara.ai/d/${slug}`;
  const registerUrl = `/register?${demo.referral_code ? `ref=${demo.referral_code}&` : ""}utm_source=demo&utm_medium=public_page&utm_content=${slug}`;
  const metaTitle = `${demo.topic} — Made with Creatara`;
  const metaDescription = demo.generated_content.replace(/\s+/g, " ").slice(0, 155);
  const typeLabel = TYPE_LABELS[demo.content_type] || demo.content_type;

  const copyContent = () => {
    navigator.clipboard.writeText(demo.generated_content);
    toast.success("Content copied!");
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Creatara" />
        <meta property="og:image" content={demo.image_url ? (demo.image_url.startsWith("http") ? demo.image_url : `https://creatara.ai${demo.image_url}`) : "https://creatara.ai/og-card.png"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={demo.image_url ? (demo.image_url.startsWith("http") ? demo.image_url : `https://creatara.ai${demo.image_url}`) : "https://creatara.ai/og-card.png"} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href={pageUrl} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <nav className="fixed top-0 w-full z-50 glass">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Creatara" className="w-10 h-10 rounded-lg" />
              <span className="text-xl font-bold">Creatara</span>
            </Link>
            <Button onClick={() => navigate(registerUrl)} className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-6" data-testid="demo-cta-top">
              Create Yours Free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </nav>

        <section className="hero-glow pt-28 pb-10 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Badge className="mb-5 bg-violet-600/10 text-violet-500 border-violet-600/20 px-4 py-1" data-testid="demo-made-with-badge">
                <Sparkles className="w-3 h-3 mr-1 inline" /> Made with Creatara
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight gradient-text mb-4" data-testid="demo-title">
                {demo.topic}
              </h1>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/20">{typeLabel}</Badge>
                {typeof demo.monetization_score === "number" && (
                  <Badge className="bg-teal-500/15 text-teal-400 border-teal-500/20" title="AI-predicted revenue potential">
                    <Zap className="w-3 h-3 mr-1 inline" />Monetisation Score {demo.monetization_score}/100
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Eye className="w-3 h-3" />{demo.views} views</span>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="pb-12 px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="glass border-white/10">
                <CardContent className="p-6 sm:p-10">
                  {demo.image_url && (
                    <img src={demo.image_url} alt={demo.topic} className="w-full rounded-xl mb-8 border border-white/10" data-testid="demo-image" />
                  )}
                  <div className="whitespace-pre-wrap text-base leading-relaxed" data-testid="demo-content">
                    {demo.generated_content}
                  </div>
                  <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
                    <span className="text-xs text-muted-foreground">Generated in seconds by Creatara</span>
                    <Button variant="ghost" size="sm" onClick={copyContent} className="text-xs" data-testid="demo-copy-btn">
                      <Copy className="w-3 h-3 mr-1" /> Copy content
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        <section className="pb-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="glass border-violet-600/20 bg-violet-600/5">
                <CardContent className="p-8 sm:p-12">
                  <h2 className="text-lg md:text-lg font-bold mb-3">Want content like this for your business?</h2>
                  <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                    Creatara writes revenue-ready posts, tracks your affiliate links, captures leads, and auto-publishes to X, LinkedIn, Bluesky and Telegram.
                  </p>
                  <Button onClick={() => navigate(registerUrl)} size="lg" className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-8 h-14 text-base" data-testid="demo-cta-bottom">
                    <Sparkles className="w-5 h-5 mr-2" /> Create Yours Free
                  </Button>
                  <p className="text-xs text-muted-foreground mt-4">No credit card required · {CLAIMS.freeAllowance.generations} free generations / {CLAIMS.freeAllowance.period}</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
